import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
  useRoomContext,
  useParticipants,
} from '@livekit/components-react';
import '@livekit/components-styles';
import {
  Phone, PhoneOff, Mic, MicOff, Video, VideoOff,
  Monitor, Clock, Users, ArrowLeft, Maximize2, Minimize2,
  Circle, Square, Upload, FileText, Star, X, Send, Paperclip, Shield,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CallTimer({ startTime }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = startTime ? new Date(startTime).getTime() : Date.now();
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [startTime]);
  const m = Math.floor(elapsed / 60), s = elapsed % 60;
  return <span data-testid="call-timer" className="font-mono text-sm tabular-nums">{String(m).padStart(2,'0')}:{String(s).padStart(2,'0')}</span>;
}

/* ──── Recording Consent Modal ──── */
function RecordingConsentModal({ bookingId, role, onClose, headers }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/calls/recording/consent/${bookingId}`, { headers })
      .then(r => r.json()).then(setStatus).catch(() => {});
  }, [bookingId]);

  const submitConsent = async (consent) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/calls/recording/consent/${bookingId}?consent=${consent}`, {
        method: 'POST', headers,
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        if (data.recording_enabled) setTimeout(onClose, 1500);
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const myConsent = role === 'expert' ? status?.expert_consent : status?.client_consent;
  const otherConsent = role === 'expert' ? status?.client_consent : status?.expert_consent;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="recording-consent-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold text-lg">Call Recording</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-zinc-400 text-sm mb-4">
          Both participants must consent for recording to be enabled. Recordings are stored securely and accessible only to call participants.
        </p>
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
            <span className="text-sm text-zinc-300">Your consent</span>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${myConsent ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
              {myConsent ? 'Agreed' : 'Pending'}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
            <span className="text-sm text-zinc-300">Other participant</span>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${otherConsent ? 'bg-green-500/20 text-green-400' : 'bg-zinc-700 text-zinc-400'}`}>
              {otherConsent ? 'Agreed' : 'Pending'}
            </span>
          </div>
        </div>
        {status?.recording_enabled ? (
          <div className="text-center p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-green-400 font-medium">Recording Enabled</p>
          </div>
        ) : !myConsent ? (
          <div className="flex gap-3">
            <button onClick={() => submitConsent(false)} disabled={loading}
              className="flex-1 py-2.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg text-sm font-medium transition-colors">
              Decline
            </button>
            <button onClick={() => submitConsent(true)} disabled={loading}
              className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition-colors">
              {loading ? 'Sending...' : 'I Consent'}
            </button>
          </div>
        ) : (
          <p className="text-center text-zinc-400 text-sm">Waiting for other participant to consent...</p>
        )}
      </div>
    </div>
  );
}

/* ──── File Share Panel ──── */
function FileSharePanel({ bookingId, headers, onClose, isGroup, eventId, isHost }) {
  const [files, setFiles] = useState([]);
  const [myFiles, setMyFiles] = useState([]);
  const [tab, setTab] = useState('call');  // 'call' or 'mine'
  const [uploading, setUploading] = useState(false);
  const [groupBroadcasting, setGroupBroadcasting] = useState(false);
  const [broadcastBusy, setBroadcastBusy] = useState(false);
  const fileInput = useRef(null);
  const groupFileInput = useRef(null);
  const broadcastInput = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/files/booking/${bookingId}`, { headers })
      .then(r => r.ok ? r.json() : []).then(setFiles).catch(() => {});
    fetch(`${API_URL}/api/files/shared`, { headers })
      .then(r => r.ok ? r.json() : []).then(setMyFiles).catch(() => {});
  }, [bookingId]);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/files/upload?context=call&booking_id=${bookingId}`, {
        method: 'POST',
        headers: { 'Authorization': headers.Authorization },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setFiles(prev => [{ ...data, uploader_name: 'You', created_at: new Date().toISOString() }, ...prev]);
      }
    } catch(e) { console.error(e); }
    setUploading(false);
  };

  // Broadcast a NEW file to all participants in the live call
  const broadcastToParticipants = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBroadcastBusy(true);
    try {
      // Fetch live participants
      const pr = await fetch(`${API_URL}/api/calls/booking/${bookingId}/participants`, { headers });
      let pids = [];
      if (pr.ok) {
        const pj = await pr.json();
        pids = (pj.participants || []).filter(Boolean);
      }
      if (pids.length === 0) {
        alert('No live participants found');
        setBroadcastBusy(false);
        return;
      }
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/dm/broadcast-file?recipient_ids=${pids.join(',')}`,
        { method: 'POST', headers: { 'Authorization': headers.Authorization }, body: fd });
      if (r.ok) {
        const d = await r.json();
        alert(`Sent free DM to ${d.sent} participant${d.sent === 1 ? '' : 's'}`);
      } else { alert('Broadcast failed'); }
    } catch(err) { console.error(err); alert('Network error'); }
    setBroadcastBusy(false);
    if (e.target) e.target.value = '';
  };

  // Send an EXISTING file from My Files to all participants
  const broadcastExistingFile = async (existingFile) => {
    setBroadcastBusy(true);
    try {
      const pr = await fetch(`${API_URL}/api/calls/booking/${bookingId}/participants`, { headers });
      let pids = [];
      if (pr.ok) {
        const pj = await pr.json();
        pids = (pj.participants || []).filter(Boolean);
      }
      if (pids.length === 0) { alert('No participants'); setBroadcastBusy(false); return; }
      // Download the file then re-upload via broadcast endpoint
      const dl = await fetch(`${API_URL}/api/files/download/${existingFile.file_id}`, { headers });
      if (!dl.ok) { alert('Cannot fetch file'); setBroadcastBusy(false); return; }
      const blob = await dl.blob();
      const fd = new FormData();
      fd.append('file', new File([blob], existingFile.original_filename || existingFile.filename || 'file'));
      const r = await fetch(`${API_URL}/api/dm/broadcast-file?recipient_ids=${pids.join(',')}`,
        { method: 'POST', headers: { 'Authorization': headers.Authorization }, body: fd });
      if (r.ok) {
        const d = await r.json();
        alert(`Sent to ${d.sent} participant${d.sent === 1 ? '' : 's'}`);
      } else { alert('Failed'); }
    } catch(err) { console.error(err); }
    setBroadcastBusy(false);
  };

  const broadcastToAttendees = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !eventId) return;
    setGroupBroadcasting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/calls/group-events/${eventId}/share-file`, {
        method: 'POST',
        headers: { 'Authorization': headers.Authorization },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        alert(`File "${data.filename}" sent free to ${data.sent} attendee${data.sent===1?'':'s'} via DM`);
      } else {
        alert('Failed to broadcast file');
      }
    } catch(e) { console.error(e); alert('Network error sending file'); }
    setGroupBroadcasting(false);
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await fetch(`${API_URL}/api/files/download/${fileId}`, { headers });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      }
    } catch(e) { console.error(e); }
  };

  return (
    <div className="fixed right-0 top-0 bottom-0 w-80 bg-zinc-900 border-l border-zinc-700 z-40 flex flex-col" data-testid="file-share-panel">
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <h3 className="text-white font-semibold">Files</h3>
        <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-zinc-800">
        <button onClick={() => setTab('call')}
          className={`flex-1 py-2.5 text-xs font-bold transition-colors ${tab === 'call' ? 'text-white border-b-2 border-purple-500' : 'text-zinc-500 hover:text-zinc-300'}`}
          data-testid="files-tab-call">Call Files</button>
        <button onClick={() => setTab('mine')}
          className={`flex-1 py-2.5 text-xs font-bold transition-colors ${tab === 'mine' ? 'text-white border-b-2 border-purple-500' : 'text-zinc-500 hover:text-zinc-300'}`}
          data-testid="files-tab-mine">My Files</button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {tab === 'call' ? (
          <>
            {files.length === 0 && <p className="text-zinc-500 text-sm text-center py-4">No files shared yet</p>}
            {files.map((f, i) => (
              <div key={f.file_id || i} className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg hover:bg-zinc-750 cursor-pointer"
                onClick={() => downloadFile(f.file_id, f.original_filename || f.filename)}>
                <FileText className="w-5 h-5 text-purple-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{f.original_filename || f.filename}</p>
                  <p className="text-xs text-zinc-500">{f.uploader_name} · {(f.size / 1024).toFixed(0)}KB</p>
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            {myFiles.length === 0 && <p className="text-zinc-500 text-sm text-center py-4">No files uploaded yet</p>}
            {myFiles.map((f, i) => (
              <div key={f.file_id || i} className="p-3 bg-zinc-800 rounded-lg space-y-2" data-testid={`my-file-${f.file_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-5 h-5 text-purple-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">{f.original_filename || f.filename}</p>
                    <p className="text-[10px] text-zinc-500">{((f.size || 0) / 1024).toFixed(0)}KB</p>
                  </div>
                </div>
                <button onClick={() => broadcastExistingFile(f)} disabled={broadcastBusy}
                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-bold rounded-lg flex items-center justify-center gap-1.5 disabled:opacity-50 transition-colors"
                  data-testid={`broadcast-existing-${f.file_id}`}>
                  {broadcastBusy ? '...' : <><Send className="w-3 h-3" /> Send Free DM to All Participants</>}
                </button>
              </div>
            ))}
          </>
        )}
      </div>
      <div className="p-4 border-t border-zinc-800 space-y-2">
        <input type="file" ref={fileInput} onChange={uploadFile} className="hidden" />
        <button
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          data-testid="upload-file-btn"
        >
          {uploading ? <span className="animate-spin">...</span> : <><Upload className="w-4 h-4" /> Share in Call</>}
        </button>
        {/* Broadcast a new file to all live participants (any call type) as FREE DM */}
        <input type="file" ref={broadcastInput} onChange={broadcastToParticipants} className="hidden" />
        <button
          onClick={() => broadcastInput.current?.click()}
          disabled={broadcastBusy}
          className="w-full flex items-center justify-center gap-2 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold transition-colors disabled:opacity-50"
          data-testid="broadcast-live-btn"
        >
          {broadcastBusy ? 'Broadcasting...' : <><Send className="w-3.5 h-3.5" /> Send to All Participants (Free DM)</>}
        </button>
        {/* Host-only: broadcast PDF/Doc free to all group session attendees via DM */}
        {isGroup && isHost && eventId && (
          <>
            <input type="file" ref={groupFileInput} onChange={broadcastToAttendees} className="hidden"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.png,.jpg,.jpeg" />
            <button
              onClick={() => groupFileInput.current?.click()}
              disabled={groupBroadcasting}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-bold transition-colors disabled:opacity-50"
              data-testid="broadcast-file-attendees-btn"
            >
              {groupBroadcasting ? 'Sending to all attendees...' : <><Send className="w-3.5 h-3.5" /> Send to All Group Attendees</>}
            </button>
            <p className="text-[10px] text-zinc-500 text-center">PDF/Doc broadcasted free to every group joiner</p>
          </>
        )}
      </div>
    </div>
  );
}

/* ──── Whiteboard Panel ──── */
function WhiteboardPanel({ onClose }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [color, setColor] = useState('#ffffff');
  const [brushSize, setBrushSize] = useState(3);
  const lastPos = useRef(null);

  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setDrawing(true);
    lastPos.current = getCoords(e);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = color;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDraw = () => { setDrawing(false); lastPos.current = null; };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    }
  }, []);

  const colors = ['#ffffff', '#ef4444', '#22c55e', '#3b82f6', '#eab308', '#a855f7', '#f97316'];

  return (
    <div className="fixed right-0 top-0 bottom-0 w-96 bg-zinc-900 border-l border-zinc-700 z-40 flex flex-col" data-testid="whiteboard-panel">
      <div className="flex items-center justify-between p-3 border-b border-zinc-800">
        <h3 className="text-white font-semibold text-sm">Whiteboard</h3>
        <div className="flex items-center gap-2">
          <button onClick={clearCanvas} className="text-xs text-zinc-400 hover:text-red-400 px-2 py-1 bg-zinc-800 rounded-lg" data-testid="clear-whiteboard">Clear</button>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
      </div>
      {/* Tools */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-800">
        {colors.map(c => (
          <button key={c} onClick={() => setColor(c)}
            className={`w-6 h-6 rounded-full border-2 transition-all ${color === c ? 'border-white scale-110' : 'border-zinc-600'}`}
            style={{ backgroundColor: c }} data-testid={`color-${c}`} />
        ))}
        <div className="flex-1" />
        <select value={brushSize} onChange={e => setBrushSize(Number(e.target.value))}
          className="bg-zinc-800 text-xs text-white rounded-lg px-2 py-1 outline-none">
          <option value={2}>Thin</option>
          <option value={3}>Medium</option>
          <option value={6}>Thick</option>
          <option value={12}>Extra</option>
        </select>
      </div>
      {/* Canvas */}
      <div className="flex-1 bg-zinc-950 cursor-crosshair">
        <canvas ref={canvasRef}
          className="w-full h-full touch-none"
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
        />
      </div>
    </div>
  );
}

/* ──── Post-Call Screen ──── */
function PostCallScreen({ booking, duration, bookingId, headers, onDone }) {
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [notes, setNotes] = useState(null);
  const [generatingNotes, setGeneratingNotes] = useState(false);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    // Fetch call notes
    fetch(`${API_URL}/api/call-notes/${bookingId}`, { headers })
      .then(r => r.ok ? r.json() : null).then(n => { if (n) setNotes(n); }).catch(() => {});
    // Fetch call files
    fetch(`${API_URL}/api/files/booking/${bookingId}`, { headers })
      .then(r => r.ok ? r.json() : []).then(setFiles).catch(() => {});
  }, [bookingId]);

  const submitRating = async () => {
    if (rating === 0) return;
    try {
      await fetch(`${API_URL}/api/calls/rating/${bookingId}?rating=${rating}&review=${encodeURIComponent(review)}`, {
        method: 'POST', headers,
      });
      setSubmitted(true);
    } catch(e) { console.error(e); }
  };

  const generateNotes = async () => {
    setGeneratingNotes(true);
    try {
      const res = await fetch(`${API_URL}/api/call-notes/generate/${bookingId}`, {
        method: 'POST', headers,
      });
      if (res.ok) {
        const notesRes = await fetch(`${API_URL}/api/call-notes/${bookingId}`, { headers });
        if (notesRes.ok) setNotes(await notesRes.json());
      }
    } catch(e) { console.error(e); }
    setGeneratingNotes(false);
  };

  const mins = Math.floor(duration / 60), secs = duration % 60;

  return (
    <div className="h-screen bg-black overflow-y-auto" data-testid="post-call-screen">
      <div className="max-w-2xl mx-auto px-6 py-12 space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
            <Phone className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold text-white">Call Completed</h1>
          <p className="text-zinc-400">{mins}m {secs}s with {booking?.expert_name || booking?.client_name}</p>
        </div>

        {/* Rating */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="rating-section">
          <h3 className="text-white font-semibold mb-3">Rate this call</h3>
          {!submitted ? (
            <>
              <div className="flex gap-2 mb-4">
                {[1,2,3,4,5].map(s => (
                  <button key={s} onClick={() => setRating(s)} data-testid={`star-${s}`}
                    className={`p-1 transition-colors ${s <= rating ? 'text-yellow-400' : 'text-zinc-600'}`}>
                    <Star className="w-8 h-8" fill={s <= rating ? 'currentColor' : 'none'} />
                  </button>
                ))}
              </div>
              <textarea value={review} onChange={e => setReview(e.target.value)}
                placeholder="Optional: Share your experience..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-sm text-white placeholder-zinc-500 resize-none h-20 mb-3" />
              <button onClick={submitRating} disabled={rating === 0}
                className="w-full py-2.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white rounded-lg text-sm font-medium transition-colors"
                data-testid="submit-rating-btn">
                Submit Rating
              </button>
            </>
          ) : (
            <p className="text-green-400 text-sm">Thank you for your rating!</p>
          )}
        </div>

        {/* AI Notes */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="notes-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">AI Call Notes</h3>
            {!notes && (
              <button onClick={generateNotes} disabled={generatingNotes}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600/20 text-purple-400 rounded-lg text-xs font-medium hover:bg-purple-600/30 transition-colors disabled:opacity-50"
                data-testid="generate-notes-btn">
                <FileText className="w-3.5 h-3.5" />
                {generatingNotes ? 'Generating...' : 'Generate Notes'}
              </button>
            )}
          </div>
          {notes?.notes ? (
            <div className="space-y-4">
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Summary</p>
                <p className="text-sm text-zinc-300">{notes.notes.summary}</p>
              </div>
              {notes.notes.topics_covered?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Topics Covered</p>
                  <div className="flex flex-wrap gap-1.5">
                    {notes.notes.topics_covered.map((t, i) => (
                      <span key={i} className="px-2 py-0.5 bg-zinc-800 text-zinc-300 text-xs rounded-full">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {notes.notes.key_takeaways?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Key Takeaways</p>
                  <ul className="space-y-1">
                    {notes.notes.key_takeaways.map((t, i) => (
                      <li key={i} className="text-sm text-zinc-300 flex gap-2">
                        <span className="text-purple-400 mt-1">•</span>{t}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {notes.notes.action_items?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Action Items</p>
                  <ul className="space-y-1.5">
                    {notes.notes.action_items.map((a, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="mt-0.5 w-5 h-5 rounded border border-zinc-600 shrink-0" />
                        <span className="text-zinc-300">{a.task} <span className="text-zinc-500">({a.for})</span></span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {notes.notes.follow_up_recommendation && (
                <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                  <p className="text-xs text-purple-400 font-medium mb-1">Follow-up Recommendation</p>
                  <p className="text-sm text-zinc-300">{notes.notes.follow_up_recommendation}</p>
                </div>
              )}
            </div>
          ) : !generatingNotes ? (
            <p className="text-zinc-500 text-sm">Click "Generate Notes" to get AI-powered call notes with action items.</p>
          ) : (
            <div className="flex items-center gap-2 text-zinc-400 text-sm">
              <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              Generating your call notes...
            </div>
          )}
        </div>

        {/* Files */}
        {files.length > 0 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="call-files-section">
            <h3 className="text-white font-semibold mb-3">Shared Files</h3>
            <div className="space-y-2">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-zinc-800 rounded-lg">
                  <Paperclip className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm text-zinc-300 flex-1 truncate">{f.original_filename}</span>
                  <span className="text-xs text-zinc-500">{(f.size / 1024).toFixed(0)}KB</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Return */}
        <button onClick={onDone}
          className="w-full flex items-center justify-center gap-2 py-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm font-medium transition-colors"
          data-testid="return-to-app-btn">
          <ArrowLeft className="w-4 h-4" /> Return to App
        </button>
      </div>
    </div>
  );
}

/* ──── Call Room Inner (with LiveKit) ──── */
function CallRoomInner({ booking, bookingId, onEndCall, callType, headers }) {
  const participants = useParticipants();
  const [showFiles, setShowFiles] = useState(false);
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingEnabled, setRecordingEnabled] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [noiseCancelEnabled, setNoiseCancelEnabled] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const noiseCancelRef = useRef(null);
  const role = booking?.client_id === JSON.parse(atob(headers.Authorization.split('.')[1])).user_id ? 'client' : 'expert';

  // Check recording consent status periodically
  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${API_URL}/api/calls/recording/consent/${bookingId}`, { headers });
        if (res.ok) {
          const data = await res.json();
          setRecordingEnabled(data.recording_enabled);
        }
      } catch {}
    };
    check();
    const iv = setInterval(check, 5000);
    return () => clearInterval(iv);
  }, [bookingId]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!recordingEnabled) {
      setShowRecordingModal(true);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: callType !== 'voice' });
      const mr = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp8,opus' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const formData = new FormData();
        formData.append('file', blob, `recording_${bookingId}.webm`);
        try {
          await fetch(`${API_URL}/api/files/upload?context=recording&booking_id=${bookingId}`, {
            method: 'POST',
            headers: { 'Authorization': headers.Authorization },
            body: formData,
          });
        } catch (e) { console.error('Recording upload failed:', e); }
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start(1000);
      mediaRecorderRef.current = mr;
      setIsRecording(true);
    } catch (e) { console.error('Recording failed:', e); }
  }, [recordingEnabled, callType, bookingId, headers]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const handleEndCall = () => {
    if (isRecording) stopRecording();
    // Cleanup noise cancellation
    if (noiseCancelRef.current) {
      noiseCancelRef.current.cleanup();
      noiseCancelRef.current = null;
    }
    onEndCall();
  };

  // Web Audio API noise cancellation toggle
  const toggleNoiseCancel = useCallback(async () => {
    if (noiseCancelEnabled && noiseCancelRef.current) {
      noiseCancelRef.current.cleanup();
      noiseCancelRef.current = null;
      setNoiseCancelEnabled(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);

      // Create noise gate using dynamics compressor + gain
      const compressor = audioCtx.createDynamicsCompressor();
      compressor.threshold.setValueAtTime(-50, audioCtx.currentTime);
      compressor.knee.setValueAtTime(40, audioCtx.currentTime);
      compressor.ratio.setValueAtTime(12, audioCtx.currentTime);
      compressor.attack.setValueAtTime(0, audioCtx.currentTime);
      compressor.release.setValueAtTime(0.25, audioCtx.currentTime);

      // High-pass filter to remove low-frequency noise
      const highpass = audioCtx.createBiquadFilter();
      highpass.type = 'highpass';
      highpass.frequency.setValueAtTime(85, audioCtx.currentTime);
      highpass.Q.setValueAtTime(0.7, audioCtx.currentTime);

      // Low-pass filter for anti-aliasing
      const lowpass = audioCtx.createBiquadFilter();
      lowpass.type = 'lowpass';
      lowpass.frequency.setValueAtTime(8000, audioCtx.currentTime);

      // Connect: source → highpass → lowpass → compressor → destination
      source.connect(highpass);
      highpass.connect(lowpass);
      lowpass.connect(compressor);

      // Create destination stream
      const dest = audioCtx.createMediaStreamDestination();
      compressor.connect(dest);

      noiseCancelRef.current = {
        audioCtx, source, stream, dest,
        cleanup: () => {
          try { source.disconnect(); highpass.disconnect(); lowpass.disconnect(); compressor.disconnect(); } catch {}
          stream.getTracks().forEach(t => t.stop());
          if (audioCtx.state !== 'closed') audioCtx.close().catch(() => {});
        }
      };
      setNoiseCancelEnabled(true);
    } catch (e) {
      console.error('Noise cancellation failed:', e);
    }
  }, [noiseCancelEnabled]);

  return (
    <div className="h-full flex flex-col bg-zinc-950" data-testid="call-room-inner">
      {/* Top Bar */}
      <div className="shrink-0 flex items-center justify-between px-4 py-2 bg-black/80 backdrop-blur-sm border-b border-zinc-800 z-10">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {isRecording && <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-xs text-zinc-400 uppercase tracking-wider font-medium">
              {booking?.is_group_call ? 'Group Call' : callType === 'voice' ? 'Voice Call' : 'Video Call'}
              {isRecording && ' · REC'}
            </span>
          </div>
          <span className="text-zinc-700">|</span>
          <div className="flex items-center gap-1.5 text-zinc-300">
            <Clock className="w-3.5 h-3.5" />
            <CallTimer startTime={booking?.call_started_at} />
          </div>
        </div>

        <div className="flex items-center gap-1">
          {/* Recording toggle */}
          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`p-2 rounded-lg transition-colors ${isRecording ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'hover:bg-zinc-800 text-zinc-400 hover:text-white'}`}
            title={isRecording ? 'Stop Recording' : recordingEnabled ? 'Start Recording' : 'Request Recording Consent'}
            data-testid="recording-toggle"
          >
            {isRecording ? <Square className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
          </button>
          {/* File share */}
          <button onClick={() => setShowFiles(!showFiles)}
            className={`p-2 rounded-lg transition-colors ${showFiles ? 'bg-purple-500/20 text-purple-400' : 'hover:bg-zinc-800 text-zinc-400 hover:text-white'}`}
            title="Share Files" data-testid="files-toggle"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          {/* Whiteboard */}
          <button onClick={() => { setShowWhiteboard(!showWhiteboard); setShowFiles(false); }}
            className={`p-2 rounded-lg transition-colors ${showWhiteboard ? 'bg-blue-500/20 text-blue-400' : 'hover:bg-zinc-800 text-zinc-400 hover:text-white'}`}
            title="Whiteboard" data-testid="whiteboard-toggle"
          >
            <Square className="w-4 h-4" />
          </button>
          {/* Participants */}
          <div className="flex items-center gap-1 px-2 text-zinc-400 text-xs">
            <Users className="w-3.5 h-3.5" />
            <span>{participants.length}</span>
          </div>
          {/* Fullscreen */}
          <button onClick={toggleFullscreen}
            className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
            data-testid="fullscreen-toggle"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          {/* Noise Cancellation */}
          <button onClick={toggleNoiseCancel}
            className={`p-2 rounded-lg transition-colors ${noiseCancelEnabled ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'hover:bg-zinc-800 text-zinc-400 hover:text-white'}`}
            title={noiseCancelEnabled ? 'Disable Noise Cancellation' : 'Enable Noise Cancellation'}
            data-testid="noise-cancel-toggle"
          >
            <Shield className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Video/Audio Area */}
      <div className="flex-1 overflow-hidden relative">
        <VideoConference />
        <RoomAudioRenderer />
      </div>

      {/* End Call */}
      <div className="shrink-0 flex items-center justify-center py-3 bg-black/80 border-t border-zinc-800">
        <button onClick={handleEndCall}
          className="flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-full text-sm font-medium transition-colors shadow-lg shadow-red-600/20"
          data-testid="end-call-btn"
        >
          <PhoneOff className="w-4 h-4" /> End Call
        </button>
      </div>

      {/* Modals */}
      {showRecordingModal && (
        <RecordingConsentModal bookingId={bookingId} role={role} onClose={() => setShowRecordingModal(false)} headers={headers} />
      )}
      {showFiles && <FileSharePanel
        bookingId={bookingId}
        headers={headers}
        onClose={() => setShowFiles(false)}
        isGroup={booking?.is_group_call}
        eventId={booking?.event_id || booking?.group_event_id || bookingId}
        isHost={booking?.is_group_call && (booking?.expert_id === JSON.parse(atob(headers.Authorization.split('.')[1])).user_id || booking?.host_id === JSON.parse(atob(headers.Authorization.split('.')[1])).user_id)}
      />}
      {showWhiteboard && <WhiteboardPanel onClose={() => setShowWhiteboard(false)} />}
    </div>
  );
}

/* ──── Main CallRoom Page ──── */
export default function CallRoom() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [serverUrl, setServerUrl] = useState('');
  const [booking, setBooking] = useState(null);
  const [callType, setCallType] = useState('video');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [callEnded, setCallEnded] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const authToken = localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  };

  const isGroupJoin = searchParams.get('group') === 'true';

  useEffect(() => {
    if (!authToken) { navigate('/login'); return; }
    joinCall();
  }, [bookingId]);

  const joinCall = async () => {
    try {
      setLoading(true);
      const endpoint = isGroupJoin
        ? `${API_URL}/api/calls/group/join/${bookingId}`
        : `${API_URL}/api/calls/join/${bookingId}`;
      const res = await fetch(endpoint, { method: 'POST', headers });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to join call');
      }
      const data = await res.json();
      setToken(data.token);
      setServerUrl(data.url);
      setBooking(data.booking);
      setCallType(data.call_type || 'video');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const endCall = async () => {
    try {
      const res = await fetch(`${API_URL}/api/calls/end/${bookingId}`, { method: 'POST', headers });
      if (res.ok) {
        const data = await res.json();
        setCallDuration(data.duration_seconds || 0);
      }
    } catch (err) { console.error('End call error:', err); }
    setCallEnded(true);
  };

  if (loading) {
    return (
      <div className="h-screen bg-black flex flex-col items-center justify-center gap-4" data-testid="call-loading">
        <div className="w-12 h-12 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-zinc-400 text-sm">Connecting to call...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen bg-black flex flex-col items-center justify-center gap-4" data-testid="call-error">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
          <PhoneOff className="w-8 h-8 text-red-500" />
        </div>
        <p className="text-white font-medium">Cannot Join Call</p>
        <p className="text-zinc-400 text-sm max-w-xs text-center">{error}</p>
        <button onClick={() => navigate('/app')}
          className="mt-4 flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-white transition-colors"
          data-testid="back-to-app-btn">
          <ArrowLeft className="w-4 h-4" /> Back to App
        </button>
      </div>
    );
  }

  if (callEnded) {
    return <PostCallScreen booking={booking} duration={callDuration} bookingId={bookingId} headers={headers} onDone={() => navigate('/app?tab=chat')} />;
  }

  if (!token || !serverUrl) {
    return (
      <div className="h-screen bg-black flex items-center justify-center">
        <p className="text-zinc-400">Missing call credentials</p>
      </div>
    );
  }

  return (
    <div className="h-screen bg-black" data-testid="call-room">
      <LiveKitRoom
        video={callType !== 'voice'}
        audio={true}
        token={token}
        serverUrl={serverUrl}
        data-lk-theme="default"
        style={{ height: '100%' }}
        onDisconnected={() => { if (!callEnded) endCall(); }}
      >
        <CallRoomInner
          booking={booking}
          bookingId={bookingId}
          onEndCall={endCall}
          callType={callType}
          headers={headers}
        />
      </LiveKitRoom>
    </div>
  );
}
